import React, {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { App } from 'antd';
import type { CreateTagPayload } from 'interfaces/FileService';
import { useTranslation } from 'react-i18next';
import { TagForm } from '../components/Forms/TagForm';
import { FilesTableEnum } from '../enums/FilesTableEnum';
import { useAuth } from '../hooks/useAuth';
import { useModal } from '../hooks/useModal';
import type File from '../interfaces/File';
import type Tag from '../interfaces/Tag';
import { FileService } from '../services/fileService';

interface TagsContextType {
  tags: Tag[];
  isLoadingTags: boolean;
  selectedTags: Tag[];
  setSelectedTags: (tags: Tag[]) => void;
  handleOpenTagForm: (tag: Tag | null) => void;
  handleDeleteTag: (tag: Tag) => void;
  createTag: (tag: Tag) => void;
  updateTag: (tag: Tag) => void;
  tagsBasedOnUser: Tag[];
  isTagDisabledForFile: (file: File, tag: Tag) => boolean;
}

export const TagsContext = createContext<TagsContextType>({
  tags: [],
  isLoadingTags: false,
  selectedTags: [],
  setSelectedTags: () => {},
  handleOpenTagForm: () => {},
  handleDeleteTag: () => {},
  createTag: () => {},
  updateTag: () => {},
  tagsBasedOnUser: [],
  isTagDisabledForFile: () => false,
});

const fileService = new FileService();

interface TagsProviderProps {
  children: ReactNode;
}

export const TagsProvider: React.FC<TagsProviderProps> = ({ children }) => {
  const { authenticatedUser, isInFileRoute, onBehalfOfUser } = useAuth();
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const modal = useModal();

  const [tags, setTags] = useState<Tag[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);

  /**
   * @description Get the tags.
   *
   */
  const getTags = useCallback(() => {
    setIsLoadingTags(true);
    fileService
      .getTags(onBehalfOfUser?.id)
      .then((response) => {
        setTags(response);
      })
      .catch(() => {
        notification.error({
          message: t('errors.hitASnag'),
          showProgress: true,
          pauseOnHover: true,
        });
      })
      .finally(() => {
        setIsLoadingTags(false);
      });
  }, [onBehalfOfUser]);

  /**
   * @description Create a tag.
   * @param {Tag} tag - The tag to create.
   */
  const createTag = useCallback(
    (tag: Tag) => {
      const payload: CreateTagPayload = {
        title: tag.title,
        userId: authenticatedUser?.id,
        color: tag.color,
        fileVisible: tag.fileVisible,
        tagVisible: tag.tagVisible,
        publicTag: tag.publicTag,
        onBehalfOf: onBehalfOfUser?.id,
      };

      setIsLoadingTags(true);
      fileService
        .createTag(payload)
        .then(() => {
          getTags();
        })
        .catch(() => {
          notification.error({
            message: t('errors.hitASnag'),
            showProgress: true,
            pauseOnHover: true,
          });
        })
        .finally(() => {
          setIsLoadingTags(false);
        });
    },
    [getTags, authenticatedUser?.id, onBehalfOfUser?.id],
  );

  /**
   * @description Update a tag.
   * @param {Tag} tag - The tag to update.
   */
  const updateTag = useCallback(
    (tag: Tag) => {
      setIsLoadingTags(true);
      const payload: CreateTagPayload = {
        id: tag.id,
        title: tag.title,
        color: tag.color,
        fileVisible: tag.fileVisible,
        tagVisible: tag.tagVisible,
        publicTag: tag.publicTag,
        onBehalfOf: onBehalfOfUser?.id,
      };
      setIsLoadingTags(true);
      fileService
        .createTag(payload)
        .then(() => {
          getTags();
        })
        .catch(() => {
          notification.error({
            message: t('errors.hitASnag'),
            showProgress: true,
            pauseOnHover: true,
          });
        })
        .finally(() => {
          setIsLoadingTags(false);
        });
    },
    [getTags, authenticatedUser?.id, onBehalfOfUser?.id],
  );

  /**
   * @description Open the tag form.
   * @param {Tag | null} tag - The tag to edit.
   */
  const handleOpenTagForm = (tag: Tag | null) => {
    modal
      .open({
        title: !!tag ? t('modal.tagForm.editTitle') : t('modal.tagForm.newTitle'),
        content: (
          <TagForm
            tag={tag}
            onSubmit={(tag) => {
              if (tag.id === 0) {
                createTag(tag);
              } else {
                updateTag(tag);
              }
              modal.close(true, []);
            }}
            onCancel={() => {
              modal.close(true, []);
            }}
          />
        ),
      })
      .then(() => {});
  };

  /**
   * @description Delete a tag.
   * @param {Tag} tag - The tag to delete.
   */
  const handleDeleteTag = (tag: Tag) => {
    modal
      .open({
        title: t('modal.deleteTag.title'),
        message: t('modal.deleteTag.description', { resource: tag.title }),
      })
      .then(() => {
        setIsLoadingTags(true);
        fileService
          .deleteTag(tag.id)
          .then(() => {
            getTags();
          })
          .catch(() => {
            notification.error({
              message: t('errors.hitASnag'),
              showProgress: true,
              pauseOnHover: true,
            });
          })
          .finally(() => {
            setIsLoadingTags(false);
          });
      });
  };

  /**
   * @description Check if the tag is disabled.
   */
  const isTagDisabledForFile = useCallback(
    (file: File, tag: Tag) => {
      const tagV2 = file?.tags?.find((t) => t.id === tag.id);
      return (
        isInFileRoute(FilesTableEnum.Received) &&
        (tagV2?.readOnly === true || tag.userId !== authenticatedUser?.id)
      );
    },
    [authenticatedUser, isInFileRoute],
  );

  /**
   * @description Get the tags on component mount.
   */
  useEffect(() => {
    if (authenticatedUser?.id) {
      getTags();
    }
  }, [authenticatedUser?.id]);

  useEffect(() => {
    if (onBehalfOfUser?.id) {
      getTags();
    }
  }, [onBehalfOfUser]);

  /**
   * @description Get the tags based on the user.
   */
  const tagsBasedOnUser = useMemo(() => {
    if (isInFileRoute(FilesTableEnum.Received)) {
      return tags.filter(
        (tag) => !(tag.publicTag === true && tag.userId === authenticatedUser?.id),
      );
    } else {
      return tags;
    }
  }, [tags, authenticatedUser]);

  const contextValue = useMemo(() => {
    return {
      tags,
      isLoadingTags,
      selectedTags,
      setSelectedTags,
      handleOpenTagForm,
      createTag,
      updateTag,
      handleDeleteTag,
      tagsBasedOnUser,
      isTagDisabledForFile,
    };
  }, [
    tags,
    isLoadingTags,
    selectedTags,
    setSelectedTags,
    handleOpenTagForm,
    createTag,
    updateTag,
    handleDeleteTag,
    tagsBasedOnUser,
    isTagDisabledForFile,
  ]);

  return <TagsContext.Provider value={contextValue}>{children}</TagsContext.Provider>;
};
